import React, { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Save } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

type TabKey = 'info' | 'permissions' | 'users'

type PermissionRow = {
  id: number
  menuName: string
  canRead: boolean
  canUpdate: boolean
  canDelete: boolean
  canInsert: boolean
}

const MODULE_OPTIONS = [
  { value: 'finance', label: 'Finance' },
  { value: 'hr', label: 'HR' },
  { value: 'security', label: 'Security' },
]

const MOCK_PERMISSIONS: PermissionRow[] = [
  { id: 1, menuName: 'Users', canRead: false, canUpdate: false, canDelete: false, canInsert: false },
  { id: 2, menuName: 'Roles', canRead: false, canUpdate: false, canDelete: false, canInsert: false },
  { id: 3, menuName: 'Approval Jobs', canRead: false, canUpdate: false, canDelete: false, canInsert: false },
]

const MOCK_ASSIGNED_USERS = [
  { fullName: '', email: '', mobileNumber: '', roleName: '', department: '' },
]

export const RoleDetail: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditMode = Boolean(id && id !== 'new')

  const [activeTab, setActiveTab] = useState<TabKey>('info')
  const [roleName, setRoleName] = useState('')
  const [status, setStatus] = useState<'Active' | 'Not Active'>('Not Active')
  const [description, setDescription] = useState('')
  const [selectedModule, setSelectedModule] = useState('')
  const [permissions, setPermissions] = useState<PermissionRow[]>(MOCK_PERMISSIONS)

  const tabClasses = (tab: TabKey) =>
    `px-4 py-2 text-sm border-b-2 transition-colors ${
      activeTab === tab ? 'border-primary bg-primary text-primary-foreground' : 'border-transparent text-muted-foreground'
    }`

  const filteredPermissions = useMemo(() => {
    if (!selectedModule) return permissions
    return permissions
  }, [permissions, selectedModule])

  const updatePermission = (idToUpdate: number, key: keyof PermissionRow, value: boolean) => {
    setPermissions((prev) => prev.map((p) => (p.id === idToUpdate ? { ...p, [key]: value } : p)))
  }

  const handleSubmit = () => {
    toast.success('UI ready. API will be added later.')
    navigate('/security/roles')
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Roles"
        description="Roles Detail"
        action={{ label: 'Submit', onClick: handleSubmit, icon: Save }}
      />

      <Card>
        <CardContent className="p-6 space-y-6">
          <div className="flex border-b">
            <button type="button" className={tabClasses('info')} onClick={() => setActiveTab('info')}>
              ROLE INFORMATION
            </button>
            <button type="button" className={tabClasses('permissions')} onClick={() => setActiveTab('permissions')}>
              ROLE PERMISSIONS
            </button>
            <button type="button" className={tabClasses('users')} onClick={() => setActiveTab('users')}>
              ASSIGNED USERS
            </button>
          </div>

          {activeTab === 'info' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Role Name *</Label>
                <Input
                  placeholder="Role Name"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  disabled={isEditMode && false}
                />
              </div>
              <div className="space-y-2">
                <Label>Status *</Label>
                <Select value={status} onValueChange={(v) => setStatus(v as 'Active' | 'Not Active')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Not Active">Not Active</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label>Description *</Label>
                <Textarea
                  placeholder="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                />
              </div>
            </div>
          )}

          {activeTab === 'permissions' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Modules</Label>
                  <Select value={selectedModule} onValueChange={setSelectedModule}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {MODULE_OPTIONS.map((m) => (
                        <SelectItem key={m.value} value={m.value}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Menu Name</TableHead>
                      <TableHead>Can Read</TableHead>
                      <TableHead>Can Update</TableHead>
                      <TableHead>Can Delete</TableHead>
                      <TableHead>Can Insert</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPermissions.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.menuName}</TableCell>
                        <TableCell>
                          <Checkbox
                            checked={row.canRead}
                            onCheckedChange={(v) => updatePermission(row.id, 'canRead', !!v)}
                          />
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            checked={row.canUpdate}
                            onCheckedChange={(v) => updatePermission(row.id, 'canUpdate', !!v)}
                          />
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            checked={row.canDelete}
                            onCheckedChange={(v) => updatePermission(row.id, 'canDelete', !!v)}
                          />
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            checked={row.canInsert}
                            onCheckedChange={(v) => updatePermission(row.id, 'canInsert', !!v)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Full Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Mobile Number</TableHead>
                    <TableHead>Role Name</TableHead>
                    <TableHead>Department</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {MOCK_ASSIGNED_USERS.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{row.fullName || '—'}</TableCell>
                      <TableCell>{row.email || '—'}</TableCell>
                      <TableCell>{row.mobileNumber || '—'}</TableCell>
                      <TableCell>{row.roleName || '—'}</TableCell>
                      <TableCell>{row.department || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
